import { ApolloClient, ApolloLink, InMemoryCache } from '@apollo/client/core'
import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { ErrorLink } from '@apollo/client/link/error'
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs'
import { VueLogger } from 'vue-logger-plugin'
import { useCookies } from 'vue3-cookies'

import { CSRF_COOKIE_NAME, CSRF_HEADER_NAME, GRAPHQL_URL } from '@/constants/appConstants'

const { cookies } = useCookies()

/** Where the error link reports GraphQL and network errors. `useApollo(logger)` replaces the console. */
let errorLogger: Pick<VueLogger, 'debug'> = console

// The CSRF cookie is set at login, after this module loads, so read it per request.
const csrfLink = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} }: { headers?: Record<string, string> }) => ({
    headers: { ...headers, [CSRF_HEADER_NAME]: cookies.get(CSRF_COOKIE_NAME) },
  }))
  return forward(operation)
})

// similar to apollo-http-link's createHttpLink, but can handle file uploads
const uploadLink = new UploadHttpLink({
  credentials: 'same-origin',
  uri: GRAPHQL_URL,
})

const errorLink = new ErrorLink(({ error }) => {
  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach(({ locations, message, path }) =>
      errorLogger.debug(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`),
    )
  } else {
    errorLogger.debug(`[Network error]: ${error}`)
  }
})

const chainedLink = ApolloLink.from([errorLink, csrfLink, uploadLink])

const apolloClient = new ApolloClient({
  cache: new InMemoryCache({
    typePolicies: {
      AliyahType: { fields: { parashah: { merge: (_existing, incoming) => incoming } } },
      AnnotationSetType: { fields: { annotations: { merge: (_existing, incoming) => incoming } } },
      BarMitzvahType: { keyFields: false },
      HaftarahType: { keyFields: false },
      ParashahType: { keyFields: false },
      Query: { fields: { barMitzvahAnnotations: { merge: (_existing, incoming) => incoming } } },
      SentenceGroupType: { keyFields: false },
    },
  }),
  defaultOptions: {
    // The store, not Apollo's cache, holds the state (ARCHITECTURE.md, store -> graphql), so every read goes to the server.
    query: { fetchPolicy: 'network-only' },
    watchQuery: { fetchPolicy: 'network-only' },
  },
  devtools: { enabled: true },
  link: chainedLink,
})

/** The one Apollo client (and cache) shared by the whole app. Passing a logger makes it the error sink. */
const useApollo = (logger: Pick<VueLogger, 'debug'> | null = null) => {
  if (logger) {
    errorLogger = logger
  }
  return { apolloClient }
}

export { useApollo }
